import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { CurrencyProvider } from './context/CurrencyContext';
import NavigationFinal from './components/NavigationFinal';
import { ExitIntentPopup } from './components/Popups';
import HomePerfect from './pages/HomePerfect';
import About from './pages/About';
import Contact from './pages/Contact';
import SpeakingLab from './pages/SpeakingLab';
import ClubFrancophone from './pages/ClubFrancophone';
import Signup from './pages/Signup';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import SupportTeacher from './pages/SupportTeacher';
import ComprehensionEcrite from './pages/ComprehensionEcrite';
import ApprendreParImage from './pages/ApprendreParImage';
import JeuxReflexion from './pages/JeuxReflexion';
import ReagisVite from './pages/ReagisVite';
import ReflexesLinguistiques from './pages/ReflexesLinguistiques';
import ScribbleDefiMots from './pages/ScribbleDefiMots';
import JeuxReflexionB2 from './pages/JeuxReflexionB2';
import LisEtReagis from './pages/LisEtReagis';
import RemetsEnOrdre from './pages/RemetsEnOrdre';
import ReflexesOrthographiques from './pages/ReflexesOrthographiques';
import LesSonsDuFrancais from './pages/LesSonsDuFrancais';
import ReconstruisLaPhrase from './pages/ReconstruisLaPhrase';
import ParleAvecDesIndices from './pages/ParleAvecDesIndices';
import LireLeFrancais from './pages/LireLeFrancais';
import ExpressionOraleGuidee from './pages/ExpressionOraleGuidee';
import ExpressionOraleLibre from './pages/ExpressionOraleLibre';
import JeuxDeRole from './pages/JeuxDeRole';
import LireComprendreA1 from './pages/LireComprendreA1';
import MaNouvelleAmie from './pages/stories/MaNouvelleAmie';
import LesMotsEnVoyage from './pages/LesMotsEnVoyage';
import Pricing from './pages/Pricing';
import { PaymentSuccess, PaymentFailure } from './pages/PaymentResult';
import TeachersPortal from './pages/TeachersPortal';
import TeacherDashboard from './pages/TeacherDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AuthCallback from './pages/AuthCallback';
import AskQuestionButton from './components/AskQuestionButton';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? children : <Navigate to="/login" />;
}

/**
 * Emergent Google Auth returns to <origin>/auth/callback#session_id=...
 * When we detect that hash we must render AuthCallback FIRST — this happens
 * synchronously during render (not in useEffect) so ProtectedRoute never
 * sees a not-yet-authenticated user and yanks them to /login.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function OAuthHashGate({ children }) {
  if (typeof window !== 'undefined' && window.location.hash && window.location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  return children;
}

function App() {
  return (
    <LanguageProvider>
      <CurrencyProvider>
      <AuthProvider>
        <Router>
        <div className="App">
          <OAuthHashGate>
          <Routes>
            <Route path="/" element={<><NavigationFinal /><HomePerfect /></>} />
            <Route path="/about" element={<><NavigationFinal /><About /></>} />
            <Route path="/contact" element={<><NavigationFinal /><Contact /></>} />
            <Route path="/speaking-lab" element={<><NavigationFinal /><SpeakingLab /></>} />

            {/* Levels — redirected to Speaking Lab (legacy URLs) */}
            <Route path="/levels/beginner" element={<Navigate to="/speaking-lab" replace />} />
            <Route path="/levels/a1" element={<Navigate to="/speaking-lab" replace />} />
            <Route path="/levels/a2" element={<Navigate to="/speaking-lab" replace />} />

            {/* Club Francophone */}
            <Route path="/club-francophone" element={<><NavigationFinal /><ClubFrancophone /></>} />

            {/* Support a Teacher */}
            <Route path="/support-a-teacher" element={<><NavigationFinal /><SupportTeacher /></>} />

            {/* Speaking Lab Modules */}
            <Route path="/speaking-lab/comprehension-ecrite" element={<><NavigationFinal /><ComprehensionEcrite /></>} />
            <Route path="/speaking-lab/comprehension-orale" element={<Navigate to="/speaking-lab" replace />} />
            <Route path="/speaking-lab/apprendre-par-image" element={<><NavigationFinal /><ApprendreParImage /></>} />
            <Route path="/speaking-lab/jeux-de-reflexion" element={<><NavigationFinal /><JeuxReflexion /></>} />
            <Route path="/speaking-lab/reagis-vite" element={<><NavigationFinal /><ReagisVite /></>} />
            <Route path="/speaking-lab/reflexes-linguistiques" element={<><NavigationFinal /><ReflexesLinguistiques /></>} />
            <Route path="/speaking-lab/scribble" element={<><NavigationFinal /><ScribbleDefiMots /></>} />
            <Route path="/speaking-lab/jeux-reflexion-b2" element={<><NavigationFinal /><JeuxReflexionB2 /></>} />
            <Route path="/speaking-lab/lis-et-reagis" element={<><NavigationFinal /><LisEtReagis /></>} />
            <Route path="/speaking-lab/remets-en-ordre" element={<><NavigationFinal /><RemetsEnOrdre /></>} />
            <Route path="/speaking-lab/reflexes-orthographiques" element={<><NavigationFinal /><ReflexesOrthographiques /></>} />
            <Route path="/speaking-lab/les-sons-du-francais" element={<><NavigationFinal /><LesSonsDuFrancais /></>} />
            <Route path="/speaking-lab/reconstruis-la-phrase" element={<><NavigationFinal /><ReconstruisLaPhrase /></>} />
            <Route path="/speaking-lab/parle-avec-des-indices" element={<><NavigationFinal /><ParleAvecDesIndices /></>} />
            <Route path="/speaking-lab/expression-orale-guidee" element={<><NavigationFinal /><ExpressionOraleGuidee /></>} />
            <Route path="/speaking-lab/expression-orale-libre" element={<><NavigationFinal /><ExpressionOraleLibre /></>} />
            <Route path="/speaking-lab/jeux-de-role" element={<><NavigationFinal /><JeuxDeRole /></>} />
            <Route path="/lire-le-francais" element={<><NavigationFinal /><LireLeFrancais /></>} />
            <Route path="/lire-le-francais/lire-et-comprendre/a1" element={<><NavigationFinal /><LireComprendreA1 /></>} />
            <Route path="/lire-le-francais/lire-et-comprendre/a1/histoires/ma-nouvelle-amie" element={<><NavigationFinal /><MaNouvelleAmie /></>} />
            <Route path="/les-mots-en-voyage" element={<><NavigationFinal /><LesMotsEnVoyage /></>} />
            <Route path="/pricing" element={<><NavigationFinal /><Pricing /></>} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/failure" element={<PaymentFailure />} />
            <Route path="/translation" element={<Navigate to="/les-mots-en-voyage" replace />} />
            <Route path="/literature" element={<Navigate to="/lire-le-francais" replace />} />

            {/* Backwards-compat redirects from old speaking-lab URLs */}
            <Route path="/speaking-lab/beginners" element={<Navigate to="/speaking-lab" replace />} />
            <Route path="/speaking-lab/beginner" element={<Navigate to="/speaking-lab" replace />} />
            <Route path="/speaking-lab/a1" element={<Navigate to="/speaking-lab" replace />} />
            <Route path="/speaking-lab/a2" element={<Navigate to="/speaking-lab" replace />} />

            {/* Auth */}
            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/dashboard" element={<ProtectedRoute><><NavigationFinal /><Dashboard /></></ProtectedRoute>} />
            <Route path="/teachers-portal" element={<><NavigationFinal /><TeachersPortal /></>} />
            <Route path="/teacher-portal" element={<Navigate to="/teachers-portal" replace />} />
            <Route path="/student-portal" element={<Navigate to="/dashboard" replace />} />
            <Route path="/teacher" element={<ProtectedRoute><><NavigationFinal /><TeacherDashboard /></></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><><NavigationFinal /><AdminDashboard /></></ProtectedRoute>} />
          </Routes>
          </OAuthHashGate>
          <ExitIntentPopup />
          <AskQuestionButton />
        </div>
      </Router>
    </AuthProvider>
    </CurrencyProvider>
    </LanguageProvider>
  );
}

export default App;
